import {VariantType} from 'notistack';
import {Group} from '@services/Table/types';
import {DrmRevisionDto} from '@core/api/back';

export type ArgumentTypes<F extends Function> = F extends (...args: infer A) => any ? A : never;

export type DefaultReducerState<Type> = {
    data: Type;
};

export type DictionaryItem = {
    value: string;
    displayName?: string;
    code?: string;
    [key: string]: any;
};

export type SelectDictionary = Record<string, DictionaryItem[]>;

export type NotificationType = {
    text?: string;
    title?: string;
    variant: VariantType;
    className?: string;
};

export type NotificationState = {
    data: NotificationType | null;
};

export interface RootState {
    menu: {
        groups: Group[];
        items: MenuItem[];
        loading: string[];
        menuLoading: boolean;
    };
    dictionaries: DefaultReducerState<SelectDictionary>;
    wellNumber: DefaultReducerState<string>;
    components: Record<string, unknown>;
    colorMode: {colorMode: 'dark' | 'light'};
    notifications: NotificationState;
    language: {data: string};
    wellIdForStatus: DefaultReducerState<string>;
}

export enum ServerMenuCode {
    WellCommonDashboard = 'wellCommonDashboard',
}

export type ServerMenu = {
    title: string;
    code?: string;
    tabs: ServerMenuTab[];
};

export enum ServerMenuTabType {
    Group = 'GROUP',
    Table = 'TABLE',
    TableV2 = 'TABLEV2',
    Files = 'FILES',
    CalendarOperations = 'OPERATIONS_CALENDAR',
    MudFactCalendar = 'MUD_FACT_CALENDAR',
    OperationDetalization = 'OPERATION_DETALIZATION',
    KnbkDrillingGroups = 'KNBK_DRILLING_GROUPS',
    InstrumentMeasure = 'INSTRUMENT_MEASURE',
    KnbkGisGroups = 'KNBK_GIS_GROUPS',
    KnbkProjectGroups = 'KNBK_PROJECT_GROUPS',
    Nested = 'NESTED',
    CasingColumnDescentProtocol = 'CASING_COLUMN_DESCENT_PROTOCOL',
    Prices = 'PRICES',
    Npv = 'NPV',
    Ggd = 'GGD',
    Trajectory = 'TRAJECTORY',
    KPODescentProtocol = 'KPO_DESCENT_PROTOCOL',
    MudAnalytics = 'MUD_ANALYTICS',
    MENU = 'MENU',
    DrillingRigInWell = 'DRILLING_RIG_IN_WELL',
    WellRisks = 'WELL_RISKS',
}

export type ServerMenuTab = {
    title: string;
    pathCode: string;
    type: ServerMenuTabType;
    findAllUrl: string | null;
    tabs?: ServerMenuTab[];
};

export type MenuItem = {
    key: string | string[];
    label: string;
    findAllUrl?: string;
    pathCode: string;
    isGroup?: boolean;
    isFiles?: boolean;
    isCalendar?: boolean;
    children?: MenuItem[];
    type: ServerMenuTabType;
    menuTypeCode?: string;
    childToRedirectTo?: MenuItem;
};

export type NestedTab = {
    key: string | string[];
    label: string;
    url: string | string[];
    isNested: boolean;
    children: MenuItem[] | undefined;
};

export type CreateData<T> = Omit<T, 'id'>;

export type ParamsType = Record<string, any>;

export type RouteParams = {
    pageId: string;
    wellId: string;
    data?: string;
    id?: string;
    groupId?: string;
    projectId?: string;
    rigId?: string;
    lasId?: string;
    pageType?: string;
};

export enum GroupElementsServiceType {
    KNBK_PLAN = 'knbk-plan',
    KNBK_FACT = 'knbk-fact',
    KNBK_PROJECT = 'knbk-project',
    KNBK_GIS = 'knbk-gis',
    CCDP = 'casing-column-descent-protocol',
    STOWAGE_REGISTER = 'stowage-registers',
    KPODP = 'kpo-descent-protocol',
    KPO_REJECTION_NKT = 'kpo-rejection-nkt',
    KPO_SCREWING_NKT = 'kpo-screwing-nkt',
    KPO_STOWAGE_REGISTER = 'kpo-stowage-registers',
}

export type SchedulerParams = {
    dateFrom: string | null;
    dateTo: string | null;
};

export type ModalData =
    | {type: 'monitoringExport'; data: null}
    | {
          type: 'witsmlConnect';
          padId: string | number;
          wellId: number;
          fieldId: number;
          connectionId: string | null;
          refetch: () => void;
      }
    | {
          type: 'drm-create';
          data: null;
          wellId: number;
      }
    | {
          type: 'drm-edit';
          data: DrmRevisionDto;
          revisionId: string;
          wellId: number;
      }
    | null;
